import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment, useState, SetStateAction, ReactElement } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { BsChevronDown } from 'react-icons/bs';
import { FaRegCircleQuestion } from 'react-icons/fa6';
import LanguageSwitcher from './LanguageSwitcher';

// Define the structure of each FAQ card
interface Card {
    title: string;
    subTitle: string;
};

type DictionaryCards = Record<string, Card>;

interface SupportCard extends Card {
    button: string;
    emailSubject: string;
    email: string;
};

// Define the structure of the FAQ section
type Faq = {
    title: string;
    subTitle: string;
    cardsLength: string;
    cards: DictionaryCards;
    supportCard: SupportCard;
};

// Define the structure of the texts prop
type Texts = {
    faq: Faq;
};

// Define the props for the FaqDesktop component
type Props = {
    texts: Texts;
};


export default function FaqDesktop({ texts }: Props) {

    const [faqIsOpen, setFaqIsOpen] = useState(false);
    const [emailBody, setEmailBody] = useState('');

    const [openIndex, setOpenIndex] = useState<string | undefined>();

    // Function to close the modal
    function closeModal() {
        setFaqIsOpen(false);
        setOpenIndex(undefined); // Reset openIndex when closing modal
    }

    // Function to open the modal
    function openModal() {
        setFaqIsOpen(true);
    }

    // Handle changes in the textarea
    const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEmailBody(event.target.value);
    };

    const faq = texts.faq;
    // Email variables
    // const emailSupportAddress = faq.supportCard.email;
    // const emailSubject = encodeURIComponent(faq.supportCard.emailSubject);
    // const emailBodyEncoded = encodeURIComponent("");
    // const mailtoLink = `mailto:${emailSupportAddress}?subject=${emailSubject}&body=${emailBodyEncoded}`;

    // Function to send the email
    const sendEmail = (mailtoLink: string) => {
        window.location.href = mailtoLink;
    };

    const renderCardQuestion = (index: string, card: Card) => {
        return (
            <button
                onClick={() => setOpenIndex(openIndex === index ? undefined : index)}
                className="flex w-full justify-between rounded-lg px-4 py-2 text-left text-sm font-medium hover:bg-primaryBlue600 focus:outline-none focus-visible:ring focus-visible:ring-blueH1 text-smokeH bg-primaryBlue700">
                <span className="w-5/6">{card.title}</span>
                <BsChevronDown
                    className={`${openIndex === index ? 'rotate-180 transform' : ''
                        } h-5 w-5 text-smokeH transition-transform duration-200`}
                />
            </button>
        )
    }

    const renderCard = (index: string, card: Card) => {
        return (
            <div key={index} className="mb-3">
                {/* FAQ Question */}
                {renderCardQuestion(index, card)}

                {/* FAQ Answer */}
                {openIndex === index &&
                    // <div className="px-4 pt-2 pb-3 text-sm text-blueH1">
                    //     {card.subTitle}
                    // </div>}
                    <div className="px-4 pt-2 pb-3 text-sm text-blueH1" dangerouslySetInnerHTML={{ __html: card.subTitle }}></div>
                }
            </div>
        )
    }

    const renderSupportCard = (card: SupportCard) => {
        const index = "supportCards"
        const mailTo = `mailto:${card.email}?subject=${encodeURIComponent(card.emailSubject)}`

        return (
            <div key={index} className="mb-3">
                {/* FAQ Question */}
                {renderCardQuestion(index, card)}

                {/* FAQ Answer */}
                {openIndex === index && <>
                    <div className="px-4 pt-2 pb-3 text-sm text-blueH1">
                        {card.subTitle}
                    </div>
                    <button
                        className="formButton flex justify-center items-center p-2 rounded-lg font-semibold w-full text-smokeH bg-primaryBlue800 hover:bg-primaryBlue950 text-lg"
                        onClick={() => sendEmail(mailTo)}
                        id="sendEmail"
                        aria-label="send email"
                    >
                        {card.button}
                    </button>
                </>}
            </div>
        )

    }

    const renderChangeLanguage = (): ReactElement => {
        return <div className='w-full flex justify-center'>
            <LanguageSwitcher />
        </div>
    }

    const renderCards = (): ReactElement => {

        const cards: ReactElement[] = [];

        Object.entries(faq.cards).forEach(([index, card]) => (
            cards.push(renderCard(index, card))
        ));

        return (
            <div className="mx-auto w-full max-w-md rounded-2xl py-2 mt-3">
                {cards}
                {renderSupportCard(faq.supportCard)}
            </div>
        )
    }

    return (
        <>
            {/* Button to Open FAQ Modal */}
            <div className="flex items-end lg:items-center justify-center h-full">
                <button
                    type="button"
                    onClick={openModal}
                    id="buttonFaq"
                    aria-label="open faq"
                    className="rounded-md text-4xl font-medium text-primaryBlue700 absolute bottom-[2rem] right-[2rem]"
                >
                    <FaRegCircleQuestion />
                </button>
            </div>

            {/* FAQ Modal */}
            <Transition appear show={faqIsOpen} as={Fragment}>
                <Dialog as="div" className="relative " style={{ zIndex: 99999 }} onClose={closeModal}>
                    {/* Overlay */}
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/25" />
                    </TransitionChild>

                    {/* Modal Content */}
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <TransitionChild
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-smokeH p-6 text-left align-middle shadow-xl transition-all">
                                    {/* Modal Header */}
                                    <div className="w-full px-4">
                                        <DialogTitle
                                            as="h3"
                                            className="text-lg font-bold leading-6 text-blueH1 flex justify-between"
                                        >
                                            {faq.title}
                                            <div
                                                className="inline-flex justify-center hover:cursor-pointer"
                                                onClick={closeModal}
                                            >
                                                <AiOutlineClose />
                                            </div>
                                        </DialogTitle>
                                        <div className="mt-2">
                                            <p className="text-sm text-blueH1">{faq.subTitle}</p>
                                        </div>

                                        {/* FAQ Items */}
                                        {renderCards()}

                                        {renderChangeLanguage()}
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog >
            </Transition >
        </>
    );
}
